import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { 
  HelpCircle, X, MessageCircle, Book, ExternalLink,
  ChevronRight, Search
} from "lucide-react";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";
import { Link, useLocation } from "react-router-dom";

// Temporary compatibility aliases for framer-motion v4 + React 19 type defs.
const MotionDiv = motion.div as any;
const MotionAnimatePresence = AnimatePresence as any;

const faqs = [
  {
    question: "What is VESTN?",
    answer: "VESTN is a tokenized real-world asset (RWA) investment platform. We enable fractional ownership of premium assets like real estate, data centers, renewable energy projects, and carbon credits through blockchain technology."
  },
  {
    question: "How do I start investing?",
    answer: "Connect your wallet, browse available assets in the marketplace, select an asset, choose your investment amount, and confirm the transaction. Your tokens will be credited to your wallet immediately."
  },
  {
    question: "What is the minimum investment?",
    answer: "Minimum investments vary by asset but typically start from $100-$500, making institutional-grade investments accessible to everyone."
  },
  {
    question: "How are returns distributed?",
    answer: "Returns are distributed directly to your wallet based on your token holdings. Frequency varies by asset type - rental income is monthly, while other yields may be quarterly."
  },
  {
    question: "Is my investment secure?",
    answer: "Yes. All assets are legally structured with proper custody arrangements. Smart contracts are audited, and we maintain regulatory compliance in all operating jurisdictions."
  },
  {
    question: "Can I sell my tokens?",
    answer: "Yes! You can trade your tokens on our secondary marketplace at any time, providing liquidity that traditional real estate investments lack."
  },
  {
    question: "What wallets are supported?",
    answer: "We support MetaMask, WalletConnect, Coinbase Wallet, and other major Web3 wallets. USDT (Tether) is the primary currency for transactions."
  },
  {
    question: "How is KYC handled?",
    answer: "KYC verification is required for investments above certain thresholds. You can complete verification in your Settings under the KYC section."
  }
];

const quickLinks = [
  { label: "How to Use Guide", href: "/how-to-use", icon: Book },
  { label: "About VESTN", href: "/about", icon: ExternalLink },
  { label: "Terms of Service", href: "/terms", icon: ExternalLink },
];

export const HelpWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const supportEmail = "support@vestn.io";

  const handleContactSupport = () => {
    const mailtoUrl = `mailto:${supportEmail}`;
    try {
      window.location.assign(mailtoUrl);
    } catch {
      const link = document.createElement("a");
      link.href = mailtoUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    // Ensure no stale overlay blocks clicks after route changes.
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!mounted) return null;

  return createPortal(
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 z-[2147483647] pointer-events-auto w-20 h-20 flex items-end justify-end",
          "print:hidden",
          isOpen && "hidden"
        )}
        type="button"
        aria-label="Open help widget"
      >
        <span className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25 flex items-center justify-center hover:scale-110 transition-transform duration-200">
          <HelpCircle className="w-6 h-6" />
        </span>
      </button>

      {/* Widget Panel */}
      <MotionAnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <MotionDiv
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2147483646] lg:hidden"
            />

            {/* Panel */}
            <MotionDiv
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={cn(
                "fixed z-[2147483647] bg-card border border-border rounded-2xl shadow-2xl overflow-hidden",
                "bottom-6 right-6 w-[380px] max-h-[600px]",
                "max-lg:inset-4 max-lg:w-auto max-lg:max-h-none max-lg:bottom-4 max-lg:right-4"
              )}
            >
              {/* Header */}
              <div className="p-4 border-b border-border bg-gradient-to-r from-primary/10 to-transparent">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Help Center</h3>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                    aria-label="Close help widget"
                  >
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search FAQs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-background"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[400px] lg:max-h-[420px]">
                {/* Quick Links */}
                <div className="p-4 border-b border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">
                    Quick Links
                  </p>
                  <div className="space-y-1">
                    {quickLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                        className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-muted transition-colors group"
                      >
                        <div className="flex items-center gap-2">
                          <link.icon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-foreground">{link.label}</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      </Link>
                    ))}
                  </div>
                </div>

                {/* FAQs */}
                <div className="p-4">
                  <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">
                    Frequently Asked Questions
                  </p>
                  {filteredFaqs.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No results found for "{searchQuery}"
                    </p>
                  ) : (
                    <Accordion type="single" collapsible className="space-y-2">
                      {filteredFaqs.map((faq, index) => (
                        <AccordionItem
                          key={index}
                          value={`item-${index}`}
                          className="border border-border rounded-lg px-3 data-[state=open]:bg-muted/30"
                        >
                          <AccordionTrigger className="text-sm text-left hover:no-underline py-3">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-sm text-muted-foreground pb-3">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-border bg-muted/30">
                <Button variant="outline" className="w-full gap-2" asChild>
                  <a
                    href={`mailto:${supportEmail}`}
                    className="w-full h-full inline-flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Contact Support
                  </a>
                </Button>
              </div>
            </MotionDiv>
          </>
        )}
      </MotionAnimatePresence>
    </>,
    document.body
  );
};
